import {
  ECrmAutoDistribution,
  EInactivationRuleMode,
  EInactivationRuleSource,
  EInactivationConditionKey,
} from './inactivation-rule.model';

/**
 * The audit trail of the person file (personservice, feature 003 F5 / 004 F1):
 * the vocabulary shared by Ajustes › Auditoria, the profile timeline and
 * Pessoas › Inativos.
 *
 * It lives here, and not in `@domains/person/profile/data`, because that lib
 * already imports this one: the audit store cannot import it back without
 * closing a cycle. The profile timeline imports these types from here.
 *
 * Wire format: the REST endpoints (`/Timeline`, `/InactivationLog`) send
 * enums by NUMBER; the OData set `AuditEvents` sends them by NAME (OData v4
 * serializes enum members as strings — same finding as
 * `channel-gateway.model.ts`). Hence a numeric enum plus a name union for each.
 */

/** `AuditOrigin` (Actuar.QueueContracts): where an audited change came from. */
export enum EAuditOrigin {
  EditProfile = 1,
  Batch = 2,
  AutomaticRule = 3,
  Import = 4,
  PartnerApi = 5,
  Integration = 6,
  SubscriptionScreen = 7,
  Migration = 8,
  Crm = 9,
  System = 10,
}

/** `AuditOrigin` by name, as the OData set `AuditEvents` sends it. */
export type TAuditOriginName = keyof typeof EAuditOrigin;

/**
 * The auditable members of the personservice `TimelineEvent` — the ones
 * `auditeventsvw` keeps — with the backend ordinals. The full enum is
 * `ETimelineEvent` in `@domains/person/profile/data`; a spec there checks the
 * two agree.
 *
 * `InactivationRulesSaved` (47) only exists in the audit view: it is never
 * written to the timeline.
 */
export enum EAuditEvent {
  CustomerInactivated = 7,
  CustomerAutoInactivated = 8,
  SubscriptionBreak = 17,
  SubscriptionDeleted = 18,
  SubscriptionEnded = 19,
  CustomerActivated = 37,
  InactivationBlocked = 38,
  InactivationChoice = 39,
  SubscriptionUnpaused = 40,
  SubscriptionFinished = 41,
  SubscriptionSwitched = 42,
  SubscriptionTransferred = 43,
  RenewalsDeleted = 44,
  AutoRenewalCanceled = 45,
  SentToCrm = 46,
  InactivationRulesSaved = 47,
}

/** `TimelineEvent` by name, as the OData set `AuditEvents` sends it. */
export type TAuditEventName = keyof typeof EAuditEvent;

/**
 * `BindingReason` (Actuar.QueueContracts): why a subscription holds the person
 * file active. By number in `InactivationAttempts`; by name inside `details`.
 */
export enum EBindingReason {
  Active = 1,
  Paused = 2,
  RenewalScheduled = 3,
  Recurring = 4,
}

/** `BindingReason` by name, as it is written inside `details`. */
export type TBindingReasonName = keyof typeof EBindingReason;

/**
 * `InactivationChoiceOption`: what the team picked in the router after a
 * blocked inactivation. By number in the `Choice` body; by name in `details`.
 */
export enum EInactivationChoiceOption {
  Pause = 1,
  Switch = 2,
  SendToCrm = 3,
  Close = 4,
  KeepActive = 5,
}

/** `InactivationChoiceOption` by name, as it is written inside `details`. */
export type TInactivationChoiceOptionName =
  keyof typeof EInactivationChoiceOption;

/** `CloseReason` (subscriptionservice, `subscriptionscloses.reason`). */
export enum ECloseReason {
  CustomerRequest = 1,
  Delinquency = 2,
  GymTermination = 3,
  UnitChange = 4,
  IncorrectEntry = 5,
  Other = 6,
  Transfer = 7,
  Switch = 8,
}

/** `CloseReason` by name, as it is written inside `details` and in `Reason`. */
export type TCloseReasonName = keyof typeof ECloseReason;

/**
 * How the last plan ended — which date its end is. A plain string in the C#
 * contracts (`InactivationLogPlanViewModel.EndReason`), with these values.
 */
export type TLastPlanEndReason =
  | 'Finished'
  | 'Closed'
  | 'Transferred'
  | 'DependentRemoved';

/*
 * `details` per event (003-data-model "details por evento", 004-plan
 * "details.snapshot"). The keys are camelCase: the backend writes them with
 * `JsonSerializerDefaults.Web`. Nested `*Utc` keys are instants and go
 * through the same conversion as the row fields; `neverSince` is an instant
 * too, despite the name (see the stores that read it).
 */

/** `details.snapshot.plan`: the last plan, as it was on the inactivation. */
export type TInactivationSnapshotPlanDetails = {
  subscriptionId: string | null;
  planId: string | null;
  name: string | null;
  endReason: TLastPlanEndReason | null;
  endOnUtc: string;
};

/** `details.snapshot` (004 F1): the person file as it was on the inactivation. */
export type TInactivationSnapshotDetails = {
  name: string | null;
  phone: string | null;
  email: string | null;
  /** The "ID" of the screens. */
  friendlyCode: string | null;
  lastAccessOnUtc: string | null;
  plan: TInactivationSnapshotPlanDetails | null;
  /** The subscriptionservice did not answer: a `null` plan means "unknown". */
  planUnavailable: boolean;
};

/** One rule condition as the routine measured it. */
export type TAutoInactivationConditionDetails = {
  key: EInactivationConditionKey;
  days: number;
  met: boolean;
  measuredOnUtc: string | null;
  /** The event never happened: since when the condition was counted (instant). */
  neverSince: string | null;
};

/** One exception the routine checked before inactivating. */
export type TAutoInactivationExceptionDetails = {
  key: 'BindingSubscription' | 'Aggregator';
  applied: boolean;
};

/** `CustomerAutoInactivated` (8). */
export type TCustomerAutoInactivatedDetails = {
  ruleVersion: number;
  mode: keyof typeof EInactivationRuleMode;
  /** Every condition of the version, met or not. */
  conditions: TAutoInactivationConditionDetails[];
  exceptionsChecked: TAutoInactivationExceptionDetails[];
  /** Missing on inactivations recorded before 004 F1. */
  snapshot?: TInactivationSnapshotDetails;
};

/** `CustomerInactivated` (7) and `CustomerActivated` (37), done by someone. */
export type TCustomerStatusChangedDetails = {
  /** The batch that changed it, when it came from one. */
  batchId?: string | null;
  /**
   * `IsActive` before the change. A string only when the snapshot was merged
   * into a `details` that was not a JSON object (`InactivationSnapshot.MergeInto`
   * keeps the old text here).
   */
  previous?: boolean | string | null;
  /** Inactivations only, and only after 004 F1. */
  snapshot?: TInactivationSnapshotDetails;
};

/** A subscription that holds the person file active. */
export type TBindingSubscriptionDetails = {
  subscriptionId: string;
  name: string | null;
  bindingReasons: TBindingReasonName[];
};

/** `InactivationBlocked` (38). */
export type TInactivationBlockedDetails = {
  attemptId: string;
  subscriptions: TBindingSubscriptionDetails[];
};

/** `InactivationChoice` (39). */
export type TInactivationChoiceDetails = {
  attemptId: string;
  option: TInactivationChoiceOptionName;
};

/** `SubscriptionEnded` (19): a closed plan. Money in cents. */
export type TSubscriptionEndedDetails = {
  closeOnUtc: string;
  originalEndOnUtc: string | null;
  reason: TCloseReasonName | null;
  motivation: string | null;
  paidBalanceCents: number;
  treatment: 'Credit' | 'Refund' | 'None';
  amountCents: number;
  recurrenceCanceled: boolean;
  /** Set when the close came from a batch. */
  originBatchItemId: string | null;
};

/** `SubscriptionFinished` (41): a plan that reached its end. */
export type TSubscriptionFinishedDetails = {
  startOnUtc: string;
  endOnUtc: string;
  renewed: boolean;
  /** What still holds the person file after this end. */
  bindingAfter: TBindingSubscriptionDetails[];
};

/** `SubscriptionBreak` (17): a pause. */
export type TSubscriptionBreakDetails = {
  startOnUtc: string;
  endOnUtc: string;
  days: number;
  pauseIndex: number;
  maxPauses: number | null;
  remainingDays: number | null;
};

/**
 * `SubscriptionUnpaused` (40). A shortened pause carries the same keys as
 * `SubscriptionBreak`; a deleted pause only `startOnUtc`, `endOnUtc`, `days`.
 */
export type TSubscriptionUnpausedDetails = {
  startOnUtc: string;
  endOnUtc: string;
  days: number;
  pauseIndex?: number;
  maxPauses?: number | null;
  remainingDays?: number | null;
};

/** `SubscriptionSwitched` (42) and `SubscriptionTransferred` (43). */
export type TSubscriptionMovedDetails = {
  fromSubscriptionId: string;
  toSubscriptionId: string;
  fromName: string | null;
  toName: string | null;
  fromPersonId: string;
  toPersonId: string;
};

/** `SubscriptionDeleted` (18): only without payments, always an incorrect entry. */
export type TSubscriptionDeletedDetails = {
  reason: TCloseReasonName;
  hadPayments: boolean;
};

/** `RenewalsDeleted` (44). */
export type TRenewalsDeletedDetails = {
  removedRenewals: number;
  totalAmountCents: number;
  hadPayments: boolean;
};

/** `AutoRenewalCanceled` (45). */
export type TAutoRenewalCanceledDetails = {
  removedRenewals: number;
  totalAmountCents: number;
};

/** `SentToCrm` (46), written by the crmservice. */
export type TSentToCrmDetails = {
  actionId: string;
  funnelId: string;
  funnelName?: string | null;
  stepId: string;
  stepName?: string | null;
  consultantId?: string | null;
  /** The inactivation this deal came from (004 F5). */
  inactivationTimelineId?: string | null;
  /** `InactivationLog` (bulk send) or `CrmAuto`; absent on the router send. */
  source?: string | null;
};

/**
 * The CRM send of a rule version (`auditeventsvw`, from
 * `db/inactivation-rules/06-crm-auto.sql`). The view writes `null` when the
 * send is off; it carries ids only — no funnel or step names.
 */
export type TInactivationRuleSavedCrmAuto = {
  funnelId: string;
  stepId: string;
  distribution: ECrmAutoDistribution;
  consultantIds: string[];
};

/** The rule fields compared by a save (FR-054). */
export type TInactivationRuleSavedSide = {
  isEnabled: boolean;
  mode: EInactivationRuleMode;
  subscriptionEndedDays: number | null;
  noAccessDays: number | null;
  noPaymentDays: number | null;
  aggregatorException: boolean;
  /**
   * `null` = no CRM send. Absent where the view predates the CRM columns:
   * then the save says nothing about the send.
   */
  crmAuto?: TInactivationRuleSavedCrmAuto | null;
};

/** `InactivationRulesSaved` (47): built by the view, not stored in the timeline. */
export type TInactivationRulesSavedDetails = {
  version: number;
  source: EInactivationRuleSource;
  /** `null` on the first version. */
  before: TInactivationRuleSavedSide | null;
  after: TInactivationRuleSavedSide;
};

/** The `details` shape of each auditable event. */
export type TAuditDetailsByEvent = {
  [EAuditEvent.CustomerInactivated]: TCustomerStatusChangedDetails;
  [EAuditEvent.CustomerAutoInactivated]: TCustomerAutoInactivatedDetails;
  [EAuditEvent.SubscriptionBreak]: TSubscriptionBreakDetails;
  [EAuditEvent.SubscriptionDeleted]: TSubscriptionDeletedDetails;
  [EAuditEvent.SubscriptionEnded]: TSubscriptionEndedDetails;
  [EAuditEvent.CustomerActivated]: TCustomerStatusChangedDetails;
  [EAuditEvent.InactivationBlocked]: TInactivationBlockedDetails;
  [EAuditEvent.InactivationChoice]: TInactivationChoiceDetails;
  [EAuditEvent.SubscriptionUnpaused]: TSubscriptionUnpausedDetails;
  [EAuditEvent.SubscriptionFinished]: TSubscriptionFinishedDetails;
  [EAuditEvent.SubscriptionSwitched]: TSubscriptionMovedDetails;
  [EAuditEvent.SubscriptionTransferred]: TSubscriptionMovedDetails;
  [EAuditEvent.RenewalsDeleted]: TRenewalsDeletedDetails;
  [EAuditEvent.AutoRenewalCanceled]: TAutoRenewalCanceledDetails;
  [EAuditEvent.SentToCrm]: TSentToCrmDetails;
  [EAuditEvent.InactivationRulesSaved]: TInactivationRulesSavedDetails;
};

/** Any `details` of an auditable event. Narrow it by the row's event. */
export type TAuditDetails = TAuditDetailsByEvent[EAuditEvent];

/** Same map, keyed by the event NAME (the OData wire format). */
export type TAuditDetailsByEventName = {
  [K in TAuditEventName]: TAuditDetailsByEvent[(typeof EAuditEvent)[K]];
};
